import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/utils';

interface ScrollShadowProps {
  children: ReactNode;
  className?: string;
  viewportClassName?: string;
  variant?: 'gray' | 'from-parent-bg';
}

export function ScrollShadow({
  children,
  className,
  viewportClassName,
  variant = 'gray',
}: ScrollShadowProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateShadows = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const maxScrollTop = viewport.scrollHeight - viewport.clientHeight;
    setCanScrollUp(viewport.scrollTop > 0);
    setCanScrollDown(viewport.scrollTop < maxScrollTop - 1);
  }, []);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    updateShadows();

    const resizeObserver = new ResizeObserver(updateShadows);
    resizeObserver.observe(viewport);

    if (viewport.firstElementChild) {
      resizeObserver.observe(viewport.firstElementChild);
    }

    return () => resizeObserver.disconnect();
  }, [updateShadows]);

  return (
    <div className={cn('relative min-h-0', className)}>
      <div
        ref={viewportRef}
        onScroll={updateShadows}
        className={cn('h-full min-h-0 overflow-auto', viewportClassName)}
      >
        {children}
      </div>
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 h-6 opacity-0 transition-opacity',
          variant === 'gray' &&
            'bg-[radial-gradient(ellipse_75%_100%_at_50%_0%,rgb(168_162_158_/_0.35),transparent_70%)]',
          variant === 'from-parent-bg' &&
            'bg-[radial-gradient(ellipse_75%_100%_at_50%_0%,var(--scroll-shadow-parent-bg,var(--background)),transparent_70%)]',
          canScrollUp && 'opacity-100',
        )}
      />
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 h-6 opacity-0 transition-opacity',
          variant === 'gray' &&
            'bg-[radial-gradient(ellipse_75%_100%_at_50%_100%,rgb(168_162_158_/_0.35),transparent_70%)]',
          variant === 'from-parent-bg' &&
            'bg-[radial-gradient(ellipse_75%_100%_at_50%_100%,var(--scroll-shadow-parent-bg,var(--background)),transparent_70%)]',
          canScrollDown && 'opacity-100',
        )}
      />
    </div>
  );
}
